import api from '../../../shared/utils/api.js'
import {
  getAllHelpRequest,
  getAllHelpSuccess,
  getAllHelpFail,
  getHelpDetailsRequest,
  getHelpDetailsSuccess,
  getHelpDetailsFail,
  createHelpRequest,
  createHelpSuccess,
  createHelpFail,
  updateHelpRequest,
  updateHelpSuccess,
  updateHelpFail,
  deleteHelpRequest,
  deleteHelpSuccess,
  deleteHelpFail,
  getAllContactMessagesRequest,
  getAllContactMessagesSuccess,
  getAllContactMessagesFail,
  getContactMessageDetailsRequest,
  getContactMessageDetailsSuccess,
  getContactMessageDetailsFail,
  updateContactMessageStatusRequest,
  updateContactMessageStatusSuccess,
  updateContactMessageStatusFail,
} from '../slices/helpSlice.js'

export const getAllHelp = (params = {}) => async dispatch => {
  try {
    dispatch(getAllHelpRequest())
    const queryString = new URLSearchParams(params).toString()
    const url = `/v1/help/admin/faqs${queryString ? `?${queryString}` : ''}`
    
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    dispatch(getAllHelpSuccess(data))
    return Promise.resolve(data)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch help items'
    dispatch(getAllHelpFail(errorMessage))
    return Promise.reject(error)
  }
}

export const getHelpDetails = helpId => async dispatch => {
  try {
    dispatch(getHelpDetailsRequest())
    const response = await api.get(`/v1/help/admin/faqs/${helpId}`)
    
    const data = response.data?.data || response.data
    
    dispatch(getHelpDetailsSuccess(data))
    return Promise.resolve(data)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch help details'
    dispatch(getHelpDetailsFail(errorMessage))
    return Promise.reject(error)
  }
}

export const createHelp = helpData => async dispatch => {
  try {
    dispatch(createHelpRequest())
    
    const payload = {
      question: helpData.question,
      answer: helpData.answer,
      category: helpData.category || 'general',
      isActive: helpData.isActive !== undefined ? helpData.isActive : true,
      priority: helpData.priority || 0,
      ...helpData
    }
    
    const response = await api.post('/v1/help/admin/faqs', payload)
    
    const data = response.data?.data || response.data
    
    const newFaq = {
      _id: data._id || data.id,
      question: data.question || payload.question,
      answer: data.answer || payload.answer,
      category: data.category || payload.category,
      isActive: data.isActive !== undefined ? data.isActive : payload.isActive,
      order: data.order || data.priority || 0,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      ...data
    }
    
    dispatch(createHelpSuccess(newFaq))
    return Promise.resolve(newFaq)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create help item'
    dispatch(createHelpFail(errorMessage))
    return Promise.reject(error)
  }
}

export const updateHelp = (helpId, helpData) => async dispatch => {
  try {
    dispatch(updateHelpRequest())
    
    const payload = {
      question: helpData.question,
      answer: helpData.answer,
      category: helpData.category,
      isActive: helpData.isActive,
      priority: helpData.priority,
      ...helpData
    }
    
    const response = await api.put(`/v1/help/admin/faqs/${helpId}`, payload)
    
    const data = response.data?.data || response.data
    
    const updatedFaq = {
      _id: helpId,
      helpId: helpId,
      question: data.question || payload.question,
      answer: data.answer || payload.answer,
      category: data.category || payload.category,
      isActive: data.isActive !== undefined ? data.isActive : payload.isActive,
      order: data.order || data.priority || 0,
      updatedAt: data.updatedAt || new Date().toISOString(),
      ...data
    }
    
    dispatch(updateHelpSuccess(updatedFaq))
    return Promise.resolve(updatedFaq)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to update help item'
    dispatch(updateHelpFail(errorMessage))
    return Promise.reject(error)
  }
}

export const deleteHelp = helpId => async dispatch => {
  try {
    dispatch(deleteHelpRequest())
    
    const response = await api.delete(`/v1/help/admin/faqs/${helpId}`)
    
    const data = response.data?.data || response.data
    
    const deleteResponse = {
      helpId: helpId,
      success: true,
      message: data.message || 'FAQ deleted successfully',
      ...data
    }
    
    dispatch(deleteHelpSuccess(deleteResponse))
    return Promise.resolve(deleteResponse)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete help item'
    dispatch(deleteHelpFail(errorMessage))
    return Promise.reject(error)
  }
}

export const bulkUpdateHelp = (helpIds, updateData) => async dispatch => {
  try {
    dispatch(updateHelpRequest())
    
    const payload = {
      helpIds,
      updateData
    }
    
    const response = await api.put('/v1/help/admin/faqs/bulk', payload)
    const data = response.data?.data || response.data
    
    dispatch(updateHelpSuccess(data))
    
    dispatch(getAllHelp())
    
    return Promise.resolve(data)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to bulk update help items'
    dispatch(updateHelpFail(errorMessage))
    return Promise.reject(error)
  }
}

export const bulkDeleteHelp = helpIds => async dispatch => {
  try {
    dispatch(deleteHelpRequest())
    
    const response = await api.delete('/v1/help/admin/faqs/bulk', {
      data: { helpIds }
    })
    
    const data = response.data?.data || response.data
    
    const deleteResponse = {
      helpIds: helpIds,
      success: true,
      message: data.message || 'FAQs deleted successfully',
      ...data
    }
    
    dispatch(deleteHelpSuccess(deleteResponse))
    
    dispatch(getAllHelp())
    
    return Promise.resolve(deleteResponse)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to bulk delete help items'
    dispatch(deleteHelpFail(errorMessage))
    return Promise.reject(error)
  }
}

export const getAllContactMessages = (params = {}) => async dispatch => {
  try {
    dispatch(getAllContactMessagesRequest())
    const queryString = new URLSearchParams(params).toString()
    const url = `/v1/help/admin/contact-messages${queryString ? `?${queryString}` : ''}`
    
    const response = await api.get(url)
    
    const data = response.data?.data || response.data
    
    dispatch(getAllContactMessagesSuccess(data))
    return Promise.resolve(data)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch contact messages'
    dispatch(getAllContactMessagesFail(errorMessage))
    return Promise.reject(error)
  }
}

export const getContactMessageDetails = messageId => async dispatch => {
  try {
    dispatch(getContactMessageDetailsRequest())
    const response = await api.get(`/v1/help/admin/contact-messages/${messageId}`)
    
    const data = response.data?.data || response.data
    
    dispatch(getContactMessageDetailsSuccess(data))
    return Promise.resolve(data)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch contact message details'
    dispatch(getContactMessageDetailsFail(errorMessage))
    return Promise.reject(error)
  }
}

export const updateContactMessageStatus = (messageId, statusData) => async dispatch => {
  try {
    dispatch(updateContactMessageStatusRequest())
    
    const payload = {
      status: statusData.status,
      notes: statusData.notes,
      assignedTo: statusData.assignedTo,
      ...statusData
    }
    
    const response = await api.put(`/v1/help/admin/contact-messages/${messageId}/status`, payload)
    
    const data = response.data?.data || response.data
    
    const updatedMessage = {
      _id: messageId,
      messageId: messageId,
      status: data.status || payload.status,
      notes: data.notes || payload.notes,
      assignedTo: data.assignedTo || payload.assignedTo,
      updatedAt: data.updatedAt || new Date().toISOString(),
      ...data
    }
    
    dispatch(updateContactMessageStatusSuccess(updatedMessage))
    return Promise.resolve(updatedMessage)
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to update contact message status'
    dispatch(updateContactMessageStatusFail(errorMessage))
    return Promise.reject(error)
  }
}